import { useMutation } from '@apollo/client';
import { List, ListItem, ListItemButton, ListItemIcon, ListItemText, Stack, useTheme } from '@mui/material';
import { language_enum } from '@predium/enums';
import { useSnackbar } from 'notistack';
import { ICONS } from '@/assets/icons';
import { useI18n } from '@/components/i18n/I18nProvider';
import Iconify from '@/components/Iconify';
import { SnackbarTimeouts } from '@/components/NotistackProvider';
import { useSession } from '@/components/session/useSession';
import { CHANGE_USER_LANGUAGE } from '@/graphql/Users.mutations';

type LanguageSelectProps = {
  handleParentMenuClose: () => void;
};

function LanguageSelect({ handleParentMenuClose }: LanguageSelectProps) {
  const theme = useTheme();
  const { t, language: currentLanguage, setLanguage } = useI18n();
  const { user } = useSession();

  const { enqueueSnackbar } = useSnackbar();

  const [changeServerLanguageForUserMutation, { loading: languageChangeLoading }] = useMutation(CHANGE_USER_LANGUAGE, {
    onCompleted: () =>
      enqueueSnackbar(t('LanguageSelect_ChangeLanguageSnackbar-Success'), {
        variant: 'success',
        autoHideDuration: SnackbarTimeouts.Success,
      }),
    onError: () =>
      enqueueSnackbar(t('LanguageSelect_ChangeLanguageSnackbar-Error'), {
        variant: 'error',
        autoHideDuration: SnackbarTimeouts.Error,
      }),
  });

  const handleLanguageChange = async (selectedLanguage: language_enum) => {
    handleParentMenuClose();
    // Make animation more fluid by adding some delay
    setLanguage(selectedLanguage);
    await changeServerLanguageForUserMutation({
      variables: {
        id: user.id,
        language: selectedLanguage,
      },
    });
  };

  const getMenuDisplay = (language: language_enum) => {
    switch (language) {
      case language_enum.deDE:
        return t('General_German');
      case language_enum.enGB:
        return t('General_English');

      default:
        const exhaustiveCheck: never = language;
        throw new Error(`Unhandled language: ${exhaustiveCheck}`);
    }
  };

  return (
    <>
      <Stack direction="column" alignContent="flex-end" sx={{ minWidth: '200px' }}>
        <List sx={{ py: 0.5 }}>
          {Object.values(language_enum)
            .sort((language1, language2) => language1.localeCompare(language2))
            .map((language) => {
              return (
                <ListItem
                  data-cy={`cy-change-language-button-${language}`}
                  key={language}
                  disablePadding
                  sx={{
                    color: 'text.primary',
                    fontSize: '14px',
                    borderRadius: '6px',
                    width: 'calc(100% - 8px)',
                    margin: theme.spacing(0.5),
                  }}
                >
                  <ListItemButton onClick={() => handleLanguageChange(language)} disabled={languageChangeLoading}>
                    <ListItemIcon>
                      <Iconify
                        icon={ICONS.CHECK}
                        color="action.active"
                        height={16}
                        width={16}
                        sx={{ visibility: currentLanguage !== language ? 'hidden' : 'visible' }}
                      />
                    </ListItemIcon>
                    <ListItemText primary={getMenuDisplay(language)} />
                  </ListItemButton>
                </ListItem>
              );
            })}
        </List>
      </Stack>
    </>
  );
}

export default LanguageSelect;
